import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'
import * as dashboardsHelpers from '@/store/dashboards/dashboards.helpers'

import Vue from 'vue'
import moment from 'moment'
import store from '@/store'
import { getWidgetType } from '@/store/dashboards/dashboards.helpers'

// Checks are performed every 3 minutes to see if widgets needs refreshing
const WIDGET_REFRESH_CHECKS_MS = 1000 * 60 * 3

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    created_at: '',
    updated_at: '',
    widgets: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param dashboard Object
 *
 * @return Object
 */
function transformCreate (dashboard) {
  return {
    id: dashboard.id,
    name: dashboard.name
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param dashboard Object
 *
 * @return Object
 */
function transformUpdate (dashboard) {
  return {
    id: dashboard.id,
    name: dashboard.name
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false,
  mode: null,
  queuedWidgetRequests: [],
  widgetRequestBusy: false,
  dashboardListing: [],
  requestTimer: null,
  refreshTimer: null,
  wizard: {
    stepNo: 0,
    steps: [], // select-widget, select-client, select-vehicle-group, select-period, set-limit, set-name, save
    selectedWidgetType: '',
    widgetPeriod: 'today',
    widgetLastXHours: 24,
    widgetLimit: 10,
    widgetName: '',
    clientId: null,
    clients: [],
    agencyId: null,
    agencies: [],
    partnerId: null,
    partners: [],
    vehicleGroupId: null,
    selectBy: ''
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  },

  getMode (state) {
    return state.mode
  },

  getWizardProps (state) {
    return state.wizard
  },

  getQueuedWidgetRequests (state) {
    return state.queuedWidgetRequests
  },

  getWidgets (state) {
    return state.data.widgets
  },

  getWidgetRequestBusy (state) {
    return state.widgetRequestBusy
  },

  getDashboardListing (state) {
    return state.dashboardListing
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`dashboards/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`dashboards/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request the first available dashboard.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFirst (context) {
    return ApiService
      .get(`dashboards/first`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('initWidgetProps')
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request the currently active dashboard.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchActive (context) {
    return ApiService
      .get(`dashboards/active`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('initWidgetProps')
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`dashboards/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let dashboard = context.getters['get']
    return ApiService
      .post(`dashboards`, transformCreate(dashboard))
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let dashboard = context.getters['get']

    return ApiService
      .put(`dashboards/${dashboard.id}`, transformUpdate(dashboard))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`dashboards/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`dashboards`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Adds a widget to the current dashboard.
   *
   * @param context Object
   *
   * @return Promise
   */
  postWidget (context) {
    const dashboard = context.getters['get']
    const widgetProps = context.getters['getWizardProps']
    const widgetType = getWidgetType(widgetProps.selectedWidgetType)

    return ApiService
      .post(`dashboards/${dashboard.id}/widgets`, widgetType.transformCreateUpdate(widgetProps))
      .then((response) => {
        context.commit('addWidget', response.data.data)
        return response
      })
  },

  /**
   * Sets the current dashboard as the active dashboard.
   *
   * @param context Object
   *
   * @return Promise
   */
  setActiveDashboard (context) {
    const dashboard = context.getters['get']
    return ApiService
      .put(`dashboards/${dashboard.id}/active`)
      .then((response) => {
        return response
      })
  },

  /**
   * Sets a different dashboard as the active dashboard.
   *
   * @param context Object
   * @param id int
   *
   * @return Promise
   */
  setActiveDashboardById (context, id) {
    return ApiService
      .put(`dashboards/${id}/active`)
      .then(() => {
        return context.dispatch(`fetchActive`)
      })
  },

  runWidgetRequests (context) {
    const widgetRequestBusy = context.getters['getWidgetRequestBusy']
    if (!widgetRequestBusy) {
      const queue = context.getters['getQueuedWidgetRequests']
      if (queue.length > 0) {
        const widget = queue[0]
        const widgetType = getWidgetType(widget.type)
        context.commit('popFromWidgetRequestQueue')

        // Check if the widget's data haven't already been loaded, if it has, then
        // check how long ago to determine if we can request it again.
        if (widget.requestState !== 'done' ||
          (widget.requestState === 'done' && (moment().unix() - widget.requestTimestamp) > widgetType.refreshSecs)) {
          context.commit('setWidgetRequestBusy', true)

          // Build the url
          const url = getWidgetType(widget.type).buildRequestUrl(widget)

          // Perform the request
          context.commit('setWidgetRequestState', { widgetId: widget.id, requestState: 'busy' })

          // IMPORTANT: In this timer interval we check if the previous request have been
          // completed or not, if the previous request have been completed this action
          // dispatches itself again. This happens until all queued requests have been completed.
          // If it is not done this way the requests are made too fast and the DTQAPI server
          // locks up (still needs to be investigated why this happens).
          context.commit('startRequestTimer')

          return ApiService
            .get(url)
            .then((response) => {
              // Set the widget's data
              context.commit('setWidgetData', { widgetId: widget.id, data: response.data.data, requestState: 'done' })
              context.commit('setWidgetRequestBusy', false)
              return response
            }).catch(() => {
              context.commit('setWidgetRequestState', { widgetId: widget.id, requestState: 'error' })
              context.commit('setWidgetRequestBusy', false)
            })
        }
      }
    }
  },

  removeWidget (context, widgetId) {
    return ApiService
      .delete(`dashboards/widgets/${widgetId}`)
      .then((response) => {
        context.commit('removeWidgetFromDashboard', widgetId)
        return response
      })
  },

  fetchDashboardListing (context) {
    return ApiService
      .get(`dashboards/listing`)
      .then((response) => {
        context.commit('setDashboardListing', response.data.data)
        context.commit('setWidgetRequestBusy', false)
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  setFormData (state, data) {
    state.form = {
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original.id = data.id
    state.original.name = data.name
    state.original.created_at = data.created_at
    state.original.updated_at = data.updated_at
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
    state.mode = null
    state.queuedWidgetRequests = []
    state.widgetRequestBusy = false
    state.dashboardListing = []
    state.requestTimer = null
    state.refreshTimer = null
    state.wizard.stepNo = 0
    state.wizard.steps = []
    state.wizard.selectedWidgetType = ''
    state.wizard.widgetPeriod = 'today'
    state.wizard.widgetLimit = 10
    state.wizard.widgetName = ''
    state.wizard.clientId = null
    state.wizard.clients = []
    state.wizard.agencyId = null
    state.wizard.agencies = []
    state.wizard.partnerId = null
    state.wizard.partners = []
    state.wizard.vehicleGroupId = null
    state.wizard.selectBy = ''
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  },

  setMode (state, mode) {
    state.mode = mode
    if (state.mode === 'create-dashboard') {
      state.data.name = ''
    }
  },

  clearMode (state) {
    state.mode = null
  },

  setWizardProp (state, { property, value }) {
    state.wizard[property] = value
  },

  resetWizardProps (state) {
    state.wizard.stepNo = 0
    state.wizard.steps = []
    state.wizard.selectedWidgetType = ''
    state.wizard.widgetPeriod = 'today'
    state.wizard.widgetLimit = 10
    state.wizard.widgetName = ''
    state.wizard.clientId = null
    state.wizard.clients = []
    state.wizard.agencyId = null
    state.wizard.agencies = []
    state.wizard.partnerId = null
    state.wizard.partners = []
    state.wizard.vehicleGroupId = null
    state.wizard.selectBy = ''
  },

  addWidget (state, widget) {
    state.data.widgets.push(widget)
  },

  pushToWidgetRequestQueue (state, widget) {
    state.queuedWidgetRequests.push(widget)
  },

  popFromWidgetRequestQueue (state) {
    state.queuedWidgetRequests.shift()
  },

  setWidgetData (state, { widgetId, data, requestState }) {
    const widgetIndex = state.data.widgets.findIndex((widget) => widget.id === widgetId)
    if (widgetIndex !== -1) {
      Vue.set(state.data.widgets[widgetIndex], 'data', data)
      Vue.set(state.data.widgets[widgetIndex], 'requestState', requestState)
      if (requestState === 'done') {
        Vue.set(state.data.widgets[widgetIndex], 'requestTimestamp', moment().unix())
      }
    }
  },

  setWidgetRequestState (state, { widgetId, requestState }) {
    const widgetIndex = state.data.widgets.findIndex((widget) => widget.id === widgetId)
    if (widgetIndex !== -1) {
      Vue.set(state.data.widgets[widgetIndex], 'requestState', requestState)
      if (requestState === 'done') {
        Vue.set(state.data.widgets[widgetIndex], 'requestTimestamp', moment().unix())
      }
    }
  },

  initWidgetProps (state) {
    for (let i = 0; i <= state.data.widgets.length - 1; i++) {
      Vue.set(state.data.widgets[i], 'data', [])
      Vue.set(state.data.widgets[i], 'requestState', 'waiting')
      Vue.set(state.data.widgets[i], 'requestTimestamp', null)
    }
  },

  setWidgetRequestBusy (state, busy) {
    state.widgetRequestBusy = busy
  },

  recoverFromOriginal (state) {
    state.data.name = state.original.name
    state.data.id = state.original.id
    state.data.created_at = state.original.created_at
    state.data.updated_at = state.original.updated_at
  },

  setDashboardListing (state, dashboardListing) {
    state.dashboardListing = dashboardListing
  },

  removeWidgetFromDashboard (state, widgetId) {
    const widgetIdx = state.data.widgets.findIndex((widget) => widget.id === widgetId)
    if (widgetIdx > -1) {
      state.data.widgets.splice(widgetIdx, 1)
    }
  },

  startRequestTimer (state) {
    state.requestTimer = setInterval(() => {
      if (!state.widgetRequestBusy) {
        clearInterval(state.requestTimer)
        state.requestTimer = null
        // Perform the next request
        store.dispatch('dashboards/dashboardsModule/runWidgetRequests')
      }
    }, 500)
  },

  stopRequestTimer (state) {
    if (state.requestTimer !== null) {
      clearInterval(state.requestTimer)
      state.requestTimer = null
    }
  },

  startRefreshTimer (state) {
    if (state.refreshTimer === null) {
      state.refreshTimer = setInterval(() => {
        // Loop through all widgets and refresh those
        // that shows today's data and have been refreshed
        // more than an hour ago
        state.data.widgets.forEach((widget) => {
          const widgetType = getWidgetType(widget.type)
          if (widget.requestTimestamp === null || (moment().unix() - widget.requestTimestamp) > widgetType.refreshSecs) {
            dashboardsHelpers.pushToWidgetRequestQueue(widget)
          }
        })

        if (state.queuedWidgetRequests.length > 0) {
          dashboardsHelpers.runWidgetRequests()
        }
      }, WIDGET_REFRESH_CHECKS_MS)
    }
  },

  stopRefreshTimer (state) {
    if (state.refreshTimer !== null) {
      clearInterval(state.refreshTimer)
      state.refreshTimer = null
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/dashboards/dashboardsModule.js